import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { 
  AcademicCapIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  TrophyIcon,
  CalendarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { RootState, AppDispatch } from '../../store'
import { createSession } from '../../store/slices/testSlice'
import { assessmentAPI, testAPI, driveAPI } from '../../services/api'
// testCompletionService removed - MySQL is now the source of truth for assessment completion
import DashboardCard from '../../components/DashboardCard'
import clsx from 'clsx'
import { formatDistanceToNow } from 'date-fns'

interface Assessment {
  id: number | string
  title: string
  description: string
  assessment_type: string
  duration_minutes: number
  total_marks: number
  is_active: boolean
  tier?: string
  driveTitle?: string
  driveStartDate?: string
  driveEndDate?: string
  driveId?: string
  driveStatus?: string
  duration?: number
  numQuestions?: number
  problems?: Array<{
    id: number
    title: string
    points: number
  }>
}

interface TestSession {
  id: number
  session_id: string
  assessment: Assessment
  assessment_title?: string
  driveTitle?: string
  status: string
  duration_minutes: number
  marks_obtained?: number
  percentage_score?: number
  created_at: string
  completed_at?: string
}

interface DashboardStats {
  totalTests: number
  completedTests: number
  averageScore: number
  bestScore: number
  upcomingTests: number
  activeTests: number
}

const CandidateDashboard: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)
  
  const [availableAssessments, setAvailableAssessments] = useState<Assessment[]>([])
  const [completedDriveAssessments, setCompletedDriveAssessments] = useState<Assessment[]>([])
  const [testHistory, setTestHistory] = useState<TestSession[]>([])
  const [registeredDrivesState, setRegisteredDrivesState] = useState<any[]>([])
  const [completedAssessmentIdsState, setCompletedAssessmentIdsState] = useState<Set<string>>(new Set())
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalTests: 0,
    completedTests: 0,
    averageScore: 0,
    bestScore: 0,
    upcomingTests: 0,
    activeTests: 0
  })
  const [isLoading, setIsLoading] = useState(true)

  // Normalize assessment id to canonical form: "<driveId>_<assessmentType>"
  const normalizeAssessmentId = (id: any) => {
    const idStr = String(id || '')
    if (idStr.startsWith('session-assessment_')) {
      const main = idStr.replace('session-assessment_', '').split('-')[0]
      return main
    }
    return idStr
  }

  useEffect(() => {
    // Clear any cached data on initial load
    localStorage.removeItem('recruiterDrives')
    localStorage.removeItem('candidateDrives')
    
    // MySQL is now the source of truth - no localStorage to clear
    
    loadDashboardData(true) // Force refresh on initial load
  }, [])

  // Removed visibility change and focus listeners to prevent API call spam

  // Removed auto-refresh to prevent API call spam

  const loadDashboardData = async (forceRefresh = false) => {
    try {
      setIsLoading(true)
      
      if (forceRefresh) {
        console.log('Force refreshing dashboard data...')
        // Clear any cached data
        localStorage.removeItem('recruiterDrives')
        // Clear browser cache for API calls
        if ('caches' in window) {
          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map(name => caches.delete(name)))
        }
      }
      
      // Get candidate's email from auth state
      const candidateEmail = user?.email
      if (!candidateEmail) {
        setAvailableAssessments([])
        setTestHistory([])
        setDashboardStats({
          totalTests: 0,
          completedTests: 0,
          averageScore: 0,
          bestScore: 0,
          upcomingTests: 0,
          activeTests: 0
        })
        return
      }

      // Get candidate's registered drives from API
      let registeredDrives = []
      try {
        // Direct API call without authentication for testing
        const timestamp = Date.now()
        const randomId = Math.random().toString(36).substring(7)
        const apiUrl = `/api/v1/candidate/drives?candidate_email=${encodeURIComponent(candidateEmail)}&t=${timestamp}&r=${randomId}`
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })
        if (response.ok) {
          const data = await response.json()
          registeredDrives = data || []
        } else {
          const errorText = await response.text()
          console.error('API call failed:', response.status, errorText)
          throw new Error(`API call failed with status: ${response.status}`)
        }
      } catch (error) {
        console.error('Failed to fetch candidate drives from API:', error)
        console.error('Error details:', error instanceof Error ? error.message : String(error))
        
        registeredDrives = []
        toast.error('Failed to load drive data. Please check your connection and try again.')
      }

      // Get assessments from registered drives, separating by drive status
      const availableAssessmentsFromDrives: any[] = []
      const completedDriveAssessmentsFromDrives: any[] = []
      const seenAssessmentIds = new Set() // Track unique assessment IDs
      const seenCompletedAssessmentIds = new Set() // Track unique completed assessment IDs
      
      registeredDrives.forEach((drive: any) => {
        
        const isCompletedDrive = drive.status === 'completed'
        const isActiveDrive = drive.status === 'active'
        
        // Read assessment configurations from drive.assessment_configs
        if (drive.assessment_configs && Object.keys(drive.assessment_configs).length > 0) {
          Object.entries(drive.assessment_configs).forEach(([assessmentType, config]: [string, any]) => {
            const assessmentId = `${drive.id}_${assessmentType}`
            
            // Map assessment type to display name
            const assessmentTypeNames: { [key: string]: string } = {
              'aptitude_reasoning': 'Aptitude & Reasoning',
              'fundamentals': 'Technical Knowledge',
              'cloud_fundamentals': 'Cloud Fundamentals',
              'presales_fundamentals': 'Pre-Sales Fundamentals',
              'english_communication': 'English Communication',
              'ai_fundamental': 'AI Fundamental',
              'coding': 'Coding Assessment',
              'case-study': 'Case Study',
              'quantitative_ability': 'Quantitative Ability',
              'logical_reasoning': 'Logical Reasoning',
              'verbal_ability': 'Verbal Ability'
            }
            
            // Ensure assessment has required properties with defaults
            const safeAssessment = {
              id: assessmentId,
              title: assessmentTypeNames[assessmentType] || assessmentType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
              description: `Selected ${config.num_questions} questions from ${assessmentTypeNames[assessmentType] || assessmentType} question bank`,
              assessment_type: assessmentType,
              duration_minutes: config.duration_minutes || 60,
              duration: config.duration_minutes || 60,
              total_marks: config.num_questions * 10 || 0, // Estimate marks based on questions
              problems: [], // Will be loaded when assessment starts
              tier: drive.tier || 'tier1',
              driveId: drive.id,
              driveTitle: drive.title || 'Untitled Drive',
              driveStartDate: drive.startDate,
              driveEndDate: drive.endDate,
              driveStatus: drive.status,
              numQuestions: config.num_questions || 0
            }
            
            // Assessment created successfully
            
            // Only show assessments for active drives
            if (isActiveDrive) {
              // Skip if we've already seen this assessment
              if (seenAssessmentIds.has(assessmentId)) {
                return
              }
              seenAssessmentIds.add(assessmentId)
              availableAssessmentsFromDrives.push(safeAssessment)
            } else if (isCompletedDrive) {
              // Move completed drives to recent tests
              if (seenCompletedAssessmentIds.has(assessmentId)) {
                return
              }
              seenCompletedAssessmentIds.add(assessmentId)
              completedDriveAssessmentsFromDrives.push(safeAssessment)
            }
          })
        }
      })

      // Get completed assessments from MySQL (production source of truth)
      let completedAssessmentIds = new Set<string>()
      try {
        const completedResponse = await assessmentAPI.getCompletedAssessments(candidateEmail)
        const mysqlCompletedIds = completedResponse.data?.completed_assessment_ids || []
        completedAssessmentIds = new Set(mysqlCompletedIds.map((id: string) => normalizeAssessmentId(id)))
        //console.log('[CandidateDashboard] Loaded completed assessments from MySQL:', Array.from(completedAssessmentIds))
      } catch (error) {
        //console.error('[CandidateDashboard] Error loading completed assessments from MySQL:', error)
        // No fallback - MySQL is the source of truth for production
        toast.error('Failed to load completed assessments. Please refresh the page.')
      }
      
      // Get test history from MySQL (combines test_sessions and assessment_responses) - using relative URL for IIS proxy
      let completedTestSessions: any[] = []
      try {
        const historyResponse = await fetch(`/api/v1/candidate/test-history?candidate_email=${encodeURIComponent(candidateEmail)}`)
        if (historyResponse.ok) {
          completedTestSessions = await historyResponse.json()
          //console.log('[CandidateDashboard] Loaded test history from MySQL:', completedTestSessions.length, 'items')
        } else {
          //console.error('[CandidateDashboard] Failed to load test history:', historyResponse.status)
        }
      } catch (error) {
        //console.error('[CandidateDashboard] Error loading test history:', error)
        // No fallback - MySQL is the source of truth for production
      }
      
      setCompletedAssessmentIdsState(completedAssessmentIds)
      
      // console.log('=== FILTERING DEBUG ===')
      // console.log('Completed assessment IDs from MySQL:', Array.from(completedAssessmentIds))
      // console.log('Available assessments from drives:', availableAssessmentsFromDrives.map(a => ({ id: a.id, title: a.title, driveId: a.driveId })))
      
      // Filter out completed/submitted assessments using normalized id
      const filteredAvailableAssessments = availableAssessmentsFromDrives.filter(assessment => {
        const normalizedId = normalizeAssessmentId(assessment.id)
        const isCompleted = completedAssessmentIds.has(normalizedId)
        
        // Also try matching with different formats
        let matchFound = isCompleted
        if (!matchFound) {
          // Try matching drive_id + assessment_type separately
          for (const completedId of completedAssessmentIds) {
            const normalizedCompleted = normalizeAssessmentId(completedId)
            if (normalizedId === normalizedCompleted || 
                normalizedId.includes(normalizedCompleted) || 
                normalizedCompleted.includes(normalizedId)) {
              matchFound = true
              break
            }
          }
        }
        
        if (matchFound) {
          //console.log(`  ❌ FILTERED OUT: "${assessment.title}" (${assessment.id} -> normalized: ${normalizedId}) - COMPLETED`)
        } else {
          //console.log(`  ✓ AVAILABLE: "${assessment.title}" (${assessment.id} -> normalized: ${normalizedId})`)
        }
        
        return !matchFound
      })
      
      // console.log(`Filtered result: ${filteredAvailableAssessments.length} available out of ${availableAssessmentsFromDrives.length} total`)
      // console.log('=== END FILTERING DEBUG ===')
      
      // Assessments processed successfully
      setAvailableAssessments(filteredAvailableAssessments)
      setCompletedDriveAssessments(completedDriveAssessmentsFromDrives)
      setTestHistory(completedTestSessions)
      setRegisteredDrivesState(registeredDrives)
      
      // Removed success toast to avoid popup spam
      
      // Calculate dashboard stats (dedupe completed by assessmentId)
      const uniqueCompletedCount = completedAssessmentIds.size
      setDashboardStats({
        totalTests: availableAssessmentsFromDrives.length,
        completedTests: completedAssessmentIds.size,
        averageScore: 0,
        bestScore: 0,
        upcomingTests: filteredAvailableAssessments.length,
        activeTests: 0
      })
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStartAssessment = async (driveId: string) => {
    try {
      // Find all assessments for this drive
      const driveAssessments = availableAssessments.filter(a => String(a.driveId) === driveId)
      
      if (driveAssessments.length === 0) {
        toast.error('No assessments found for this drive')
        return
      }

      // Check if any assessment's drive is completed
      if (driveAssessments.some(a => a.driveStatus === 'completed')) {
        toast.error('This drive is no longer available. The drive has been completed.')
        return
      }

      // Store assessment sequence in sessionStorage for continuous flow
      const assessmentSequence = driveAssessments.map(a => ({
        id: String(a.id),
        title: a.title,
        assessment_type: a.assessment_type,
        duration_minutes: a.duration_minutes || a.duration || 30,
        numQuestions: a.numQuestions || 1,
        driveId: a.driveId
      }))

      sessionStorage.setItem('currentDriveAssessmentSequence', JSON.stringify(assessmentSequence))
      sessionStorage.setItem('currentDriveAssessmentIndex', '0')
      sessionStorage.setItem('currentDriveId', driveId)

      // console.log('[CandidateDashboard] Stored assessment sequence:', {
      //   driveId,
      //   sequenceLength: assessmentSequence.length,
      //   assessments: assessmentSequence.map((a: any) => ({ id: a.id, title: a.title })),
      //   initialIndex: '0'
      // })

      // Start the first assessment in the sequence
      const firstAssessment = assessmentSequence[0]
      await startNextAssessment(firstAssessment, 0)
    } catch (error) {
      console.error('Failed to start drive assessments:', error)
      toast.error('Failed to start assessments')
    }
  }

  const startNextAssessment = async (assessment: any, index: number) => {
    try {
      // Parse assessment ID to get drive ID and assessment type
      // Format: "driveId_assessmentType" (e.g., "3_aptitude_reasoning")
      // Split only at the FIRST underscore so multi-word types like "quantitative_ability" are preserved
      const firstUnderscore = assessment.id.indexOf('_')
      const driveId = assessment.id.substring(0, firstUnderscore)
      const assessmentType = assessment.id.substring(firstUnderscore + 1)
      
      // Get drive configuration from the assessment data
      const numQuestions = assessment.numQuestions || 1
      const durationMinutes = assessment.duration_minutes || 30
      
      // console.log('=== QUESTION GENERATION DEBUG ===')
      // console.log('Assessment ID (full):', assessment.id)
      // console.log('Drive ID:', driveId)
      // console.log('Assessment Type:', assessmentType, '(type:', typeof assessmentType, ')')
      // console.log('Number of Questions:', numQuestions)
      // console.log('Duration Minutes:', durationMinutes)
      // console.log('Assessment index:', index)
      
      // Generate questions dynamically from question bank
      // console.log('Calling assessmentAPI.generateQuestions with:', {
      //   driveId,
      //   assessmentType,
      //   numQuestions,
      //   durationMinutes
      // })
      
      const questionsResponse = await assessmentAPI.generateQuestions(
        driveId,
        assessmentType,
        numQuestions,
        durationMinutes
      )
      
      //console.log('assessmentAPI.generateQuestions response:', questionsResponse)
      
      const generatedQuestions = questionsResponse.data.questions
      
      //console.log('Generated questions count:', generatedQuestions.length)
      //console.log('Generated questions:', generatedQuestions)
      
      // Create session with generated questions
      const response = await dispatch(createSession({
        assessment_id: assessment.id, // Pass the full assessment ID string
        questions: generatedQuestions,
        duration_minutes: durationMinutes,
        assessment_type: assessmentType,
        candidate_email: user?.email // Add candidate email
      }))
      if (response.payload) {
        const session = response.payload as any
        navigate(`/test/${session.session_id}/instructions`)
      }
    } catch (error) {
      console.error('Failed to create session:', error)
      toast.error('Failed to start assessment')
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      completed: { color: 'bg-success-100 text-success-800', icon: CheckCircleIcon },
      started: { color: 'bg-primary-100 text-primary-800', icon: PlayIcon },
      expired: { color: 'bg-danger-100 text-danger-800', icon: XCircleIcon },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: XCircleIcon }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.cancelled
    const Icon = config.icon
    
    return (
      <span className={clsx('inline-flex items-center px-2 py-1 rounded text-xs font-medium', config.color)}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const getTierBadge = (tier?: string) => {
    if (!tier) return null
    
    const tierColors = {
      tier1: 'bg-blue-100 text-blue-800',
      tier2: 'bg-purple-100 text-purple-800'
    }
    
    return (
      <span className={clsx('inline-flex items-center px-2 py-1 rounded text-xs font-medium', tierColors[tier as keyof typeof tierColors] || 'bg-gray-100 text-gray-800')}>
        {tier.toUpperCase()}
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Welcome back, {user?.first_name}!
              </h1>
              <p className="text-gray-600 mt-1 text-sm">
                Track your progress and start new assessments
              </p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="bg-white rounded-full p-2 shadow-lg">
                <AcademicCapIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div className="bg-white rounded-xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total Tests</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{dashboardStats.totalTests}</p>
              </div>
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-3">
                <AcademicCapIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Completed</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{dashboardStats.completedTests}</p>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-3">
                <CheckCircleIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Available Assessments */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">
                  Available Assessments
                </h2>
                <div className="bg-blue-100 rounded-full px-3 py-1">
                  <span className="text-blue-800 font-semibold text-xs">
                    {availableAssessments.length} Available
                  </span>
                </div>
              </div>
              
              {availableAssessments.length === 0 ? (
                <div className="text-center py-8">
                  <div className="bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <AcademicCapIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">No Assessments Available</h3>
                  <p className="text-gray-500 text-sm mb-3">You haven't registered for any drives yet</p>
                  <div className="bg-blue-50 rounded-lg p-3 max-w-md mx-auto">
                    <p className="text-xs text-blue-800">
                      💡 <strong>Tip:</strong> Register for a drive using the link provided by your recruiter to access assessments
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    // Group assessments by drive
                    const drivesMap = new Map<string, Assessment[]>()
                    availableAssessments.forEach(assessment => {
                      const driveId = String(assessment.driveId || 'unknown')
                      if (!drivesMap.has(driveId)) {
                        drivesMap.set(driveId, [])
                      }
                      drivesMap.get(driveId)!.push(assessment)
                    })

                    return Array.from(drivesMap.entries()).map(([driveId, driveAssessments]) => {
                      const driveTitle = driveAssessments[0]?.driveTitle || 'Untitled Drive'
                      const completedCount = driveAssessments.filter(a => 
                        completedAssessmentIdsState.has(String(a.id))
                      ).length
                      const totalCount = driveAssessments.length
                      const allCompleted = completedCount === totalCount
                      const hasIncomplete = completedCount < totalCount
                      
                      // Check if assessment sequence is active for this drive OR if drive was left
                      const currentDriveId = sessionStorage.getItem('currentDriveId')
                      const assessmentSequenceStarted = sessionStorage.getItem('assessmentSequenceStarted') === 'true'
                      const driveLeft = sessionStorage.getItem(`drive_${driveId}_left`) === 'true'
                      const isAssessmentActive = (assessmentSequenceStarted && currentDriveId === driveId) || driveLeft

                      return (
                        <div
                          key={driveId}
                          className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue-300 transition-all duration-300"
                        >
                          {/* Drive Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg p-1.5">
                                  <AcademicCapIcon className="w-4 h-4 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {driveTitle}
                                </h3>
                                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                  Active
                                </span>
                              </div>
                              <div className="ml-7 text-xs text-gray-600">
                                {completedCount} of {totalCount} assessments completed
                              </div>
                            </div>
                            <div className="ml-3">
                              {allCompleted ? (
                                <button
                                  disabled
                                  className="bg-gray-300 text-gray-500 px-4 py-2 rounded-lg font-medium flex items-center space-x-1.5 text-sm cursor-not-allowed"
                                >
                                  <CheckCircleIcon className="w-4 h-4" />
                                  <span>All Completed</span>
                                </button>
                              ) : isAssessmentActive ? (
                                <button
                                  disabled
                                  className="bg-orange-300 text-orange-700 px-4 py-2 rounded-lg font-medium flex items-center space-x-1.5 text-sm cursor-not-allowed"
                                  title={driveLeft ? "You have left this test. All assessments have been submitted." : "Assessment in progress. Complete all assessments in one go."}
                                >
                                  <PlayIcon className="w-4 h-4" />
                                  <span>{driveLeft ? "Test Left" : "Test In Progress"}</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleStartAssessment(driveId)}
                                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-1.5 shadow-md hover:shadow-lg transition-all duration-300 text-sm"
                                >
                                  <PlayIcon className="w-4 h-4" />
                                  <span>Start Test</span>
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Assessment List */}
                          <div className="ml-7 space-y-2 border-t border-gray-200 pt-3">
                            {driveAssessments.map((assessment) => {
                              const isCompleted = completedAssessmentIdsState.has(String(assessment.id))
                              return (
                                <div
                                  key={assessment.id}
                                  className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-100"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      {isCompleted ? (
                                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                                      ) : (
                                        <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                                      )}
                                      <span className={`text-sm font-medium ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                        {assessment.title}
                                      </span>
                                    </div>
                                    <div className="ml-6 flex items-center flex-wrap gap-2 text-xs text-gray-500">
                                      <div className="flex items-center space-x-1">
                                        <ClockIcon className="w-3 h-3" />
                                        <span>{assessment.duration_minutes || assessment.duration || '60'}m</span>
                                      </div>
                                      {assessment.numQuestions && (
                                        <div className="flex items-center space-x-1">
                                          <span>{assessment.numQuestions} questions</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity & Performance */}
          <div className="space-y-5">
            {/* Recent Test History */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-gray-900">
                  Recent Tests
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate('/test-history')}
                    className="text-blue-600 hover:text-blue-800 text-xs font-medium underline"
                  >
                    View All
                  </button>
                  <div className="bg-green-100 rounded-full p-1.5">
                    <CalendarIcon className="w-4 h-4 text-green-600" />
                  </div>
                </div>
              </div>
              
              {testHistory.length === 0 && completedDriveAssessments.length === 0 ? (
                <div className="text-center py-8">
                  <div className="bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                    <CalendarIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm">No Tests Taken Yet</h4>
                  <p className="text-gray-500 text-xs">Start your first assessment to see your progress here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Show recent test history */}
                  {testHistory.slice(0, 5).map((session) => (
                    <div
                      key={session.id}
                      className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="bg-blue-100 rounded-lg p-1">
                              <AcademicCapIcon className="w-3 h-3 text-blue-600" />
                            </div>
                            <p className="font-semibold text-gray-900 text-sm truncate">
                              {session.assessment?.title || session.assessment_title || 'Assessment'}
                            </p>
                          </div>
                          <div className="flex items-center space-x-3 mb-2">
                            {getStatusBadge(session.status)}
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-600">
                            <div className="flex items-center space-x-1">
                              <ClockIcon className="w-3 h-3" />
                              <span>{session.duration_minutes || 0} minutes</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <CalendarIcon className="w-3 h-3" />
                              <span>
                                {formatDistanceToNow(new Date(session.completed_at || session.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          {session.driveTitle && (
                            <p className="text-xs text-gray-500 mt-1">
                              From: {session.driveTitle}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Show completed drive assessments if no test history - but only for drives the user was registered for */}
                  {testHistory.length === 0 && completedDriveAssessments
                    .filter(assessment => {
                      // Only show assessments from drives the user was actually registered for
                      return registeredDrivesState.some(drive => 
                        drive.id === assessment.driveId && 
                        drive.status === 'completed'
                      )
                    })
                    .map((assessment) => (
                    <div
                      key={`completed-${assessment.id}`}
                      className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300 rounded-xl p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <div className="bg-gray-400 rounded-lg p-1">
                              <AcademicCapIcon className="w-3 h-3 text-white" />
                            </div>
                            <p className="font-semibold text-gray-700 text-sm truncate">
                              {assessment.title}
                            </p>
                          </div>
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700">
                            <CheckCircleIcon className="w-3 h-3 mr-1" />
                            Drive Completed
                          </span>
                          {assessment.driveTitle && (
                            <p className="text-xs text-gray-500 mt-1.5">
                              From: {assessment.driveTitle}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>


            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-gray-900">
                  Quick Actions
                </h3>
                <div className="bg-orange-100 rounded-full p-1.5">
                  <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/profile')}
                  className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border border-blue-200 rounded-lg p-3 text-left transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-2">
                    <div className="bg-blue-100 rounded-lg p-1.5 group-hover:bg-blue-200 transition-colors">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Update Profile</p>
                      <p className="text-xs text-gray-500">Manage your personal information</p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    toast('Certificate management feature is coming soon!', {
                      icon: '🏆',
                      style: {
                        borderRadius: '10px',
                        background: '#363636',
                        color: '#fff',
                      },
                    })
                  }}
                  className="w-full bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border border-green-200 rounded-lg p-3 text-left transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-2">
                    <div className="bg-green-100 rounded-lg p-1.5 group-hover:bg-green-200 transition-colors">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">View Certificates</p>
                      <p className="text-xs text-gray-500">Download your achievement certificates</p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    toast('Support feature is coming soon!', {
                      icon: '🎧',
                      style: {
                        borderRadius: '10px',
                        background: '#363636',
                        color: '#fff',
                      },
                    })
                  }}
                  className="w-full bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-purple-200 rounded-lg p-3 text-left transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-2">
                    <div className="bg-purple-100 rounded-lg p-1.5 group-hover:bg-purple-200 transition-colors">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Get Support</p>
                      <p className="text-xs text-gray-500">Need help? Contact our support team</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CandidateDashboard
